'use client'

import { memo } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { ThemeToggle } from '../theme/ThemeToggle';
import { Code2, LogOut, Menu, Settings, Search, BookmarkCheck, History, Mail, LucideIcon, X } from 'lucide-react';
import { useAuth } from '@/components/auth/AuthProvider';
import { cn } from '@/lib/utils';
import { GithubSignInButton } from '../auth/github-sign-in-button';
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetPortal,
  SheetOverlay,
  SheetClose
} from "@/components/ui/sheet";
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { IconWithTooltip } from '@/components/ui/icon-with-tooltip';
import { Typography } from '@/components/ui/typography';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface SharedListInfo {
  name: string;
  user_id: string;
  user: Array<{
    user_metadata: {
      full_name?: string;
      name?: string;
    };
  }>;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: LucideIcon;
  highlight?: boolean;
}

const Header = memo(function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const isSharedPage = location.pathname.startsWith('/shared/');
  const shareId = isSharedPage ? location.pathname.split('/').pop() : null;

  const { data: sharedListInfo } = useQuery({
    queryKey: ['sharedList', shareId],
    queryFn: async () => {
      if (!shareId) return null;

      const { data: sharedListData, error: sharedError } = await supabase
        .from('shared_lists')
        .select(`
          name,
          user_id,
          user:profiles(user_metadata)
        `)
        .eq('share_id', shareId)
        .single();

      if (sharedError) {
        console.error('Error fetching shared list:', sharedError);
        throw sharedError;
      }

      return sharedListData as SharedListInfo;
    },
    enabled: isSharedPage && !!shareId
  });

  const handleLogoClick = () => {
    navigate('/', { replace: true });
    setSearchParams({}, { replace: true });
  };

  const navigation: NavigationItem[] = [
    { name: 'Search', href: '/search', icon: Search },
    { name: 'Saved Profiles', href: '/saved-profiles', icon: BookmarkCheck },
    { name: 'Saved Searches', href: '/saved-searches', icon: History },
    { name: 'Gmail', href: '/gmail', icon: Mail },
  ];

  const NavLinks = () => (
    <>
      {navigation.map((item) => {
        const isActive = location.pathname === item.href;
        return (
          <NavLink
            key={item.name}
            to={item.href}
            onClick={() => {
              setSearchParams({}, { replace: true });
            }}
            className={cn(
              "flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors duration-200",
              isActive 
                ? "bg-primary text-primary-foreground font-medium w-auto" 
                : "text-muted-foreground hover:text-foreground w-10 md:w-auto",
              "w-full"
            )}
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            <span className={cn(
              "text-sm font-medium whitespace-nowrap",
              isActive ? "block" : "hidden md:block"
            )}>
              {item.name}
            </span>
          </NavLink>
        );
      })}
    </>
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  if (!user) {
    return (
      <header className="sticky top-4 z-[100] w-full mx-auto max-w-4xl">
        <div className="mx-auto bg-background/95 dark:bg-background/80 backdrop-blur-lg border-x border-b border-border/40 dark:border-border/20 rounded-full shadow-lg dark:shadow-2xl dark:shadow-violet-500/5">
          <div className="container flex h-14 sm:h-16 items-center justify-between px-6">
            <div className="flex items-center">
              <NavLink 
                to="/home"
                className="flex items-center gap-2 md:gap-3 hover:opacity-90 transition-all duration-200 cursor-pointer group"
                onClick={handleLogoClick}
              >
                <IconWithTooltip
                  icon={<Code2 className="h-5 w-5 md:h-6 md:w-6 text-primary group-hover:rotate-12 transition-transform duration-200" />}
                  tooltip="Go to Home"
                  isClickable
                />
                <div className="flex flex-col justify-center">
                  <Typography variant="large" as="span" className="font-poppins font-bold tracking-tight text-primary leading-none">
                    DevFinder
                  </Typography>
                  <Typography variant="small" as="span" className="hidden sm:block font-inter text-muted-foreground mt-0.5">
                    Find Your Next Developer
                  </Typography>
                </div>
              </NavLink>
            </div>
            
            <div className="flex flex-1 items-center justify-end space-x-2">
              <ThemeToggle />
              <GithubSignInButton className="hidden sm:flex h-10 text-sm font-medium" />
              <GithubSignInButton 
                variant="icon"
                className="sm:hidden h-10 text-sm font-medium"
              />
            </div>
          </div>
        </div>
      </header>
    );
  }

  if (isSharedPage) {
    return (
      <header className="sticky top-4 z-[100] w-full mx-auto max-w-4xl">
        <div className="mx-auto bg-background/95 dark:bg-background/80 backdrop-blur-lg border-x border-b border-border/40 dark:border-border/20 rounded-full shadow-lg dark:shadow-2xl dark:shadow-violet-500/5">
          <div className="container flex h-14 sm:h-16 items-center px-6">
            <div className="mr-4 flex">
              <NavLink 
                to="/home"
                className="flex items-center gap-2 md:gap-3 hover:opacity-90 transition-all duration-200 cursor-pointer group"
                onClick={handleLogoClick}
              >
                <IconWithTooltip
                  icon={<Code2 className="h-5 w-5 md:h-6 md:w-6 text-primary group-hover:rotate-12 transition-transform duration-200" />}
                  tooltip="Go to Home"
                  isClickable
                />
                <div className="flex flex-col justify-center">
                  <Typography variant="large" as="span" className="font-poppins font-bold tracking-tight text-primary leading-none">
                    DevFinder
                  </Typography>
                  <Typography variant="small" as="span" className="hidden sm:block font-inter text-muted-foreground mt-0.5">
                    Find Your Next Developer
                  </Typography>
                </div>
              </NavLink>
            </div>

            {isSharedPage && sharedListInfo && (
              <div className="flex items-center space-x-4">
                <div className="text-sm text-muted-foreground">
                  <div className="font-medium">
                    {sharedListInfo.name}
                  </div>
                  <div className="text-xs">
                    Shared by {sharedListInfo.user[0]?.user_metadata?.full_name || sharedListInfo.user[0]?.user_metadata?.name || 'Unknown'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="sticky top-4 z-[100] w-full mx-auto max-w-4xl">
      <div className="mx-auto bg-background/95 dark:bg-background/80 backdrop-blur-lg border-x border-b border-border/40 dark:border-border/20 rounded-full shadow-lg dark:shadow-2xl dark:shadow-violet-500/5">
        <div className="container flex h-14 sm:h-16 items-center justify-between px-6">
          <div className="flex items-center">
            <NavLink 
              to="/home"
              className="flex items-center gap-2 md:gap-3 hover:opacity-90 transition-all duration-200 cursor-pointer group"
              onClick={handleLogoClick}
            >
              <IconWithTooltip
                icon={<Code2 className="h-5 w-5 md:h-6 md:w-6 text-primary group-hover:rotate-12 transition-transform duration-200" />}
                tooltip="Go to Home"
                isClickable
              />
              <div className="flex flex-col justify-center">
                <Typography variant="large" as="span" className="font-poppins font-bold tracking-tight text-primary leading-none">
                  DevFinder
                </Typography>
                <Typography variant="small" as="span" className="hidden sm:block font-inter text-muted-foreground mt-0.5">
                  Find Your Next Developer
                </Typography>
              </div>
            </NavLink>
          </div>
          
          <div className="flex flex-1 items-center justify-end space-x-2">
            <nav className="hidden md:flex items-center space-x-2">
              <NavLinks />
            </nav>
            <div className="flex items-center gap-2 md:gap-4 ml-auto">
              <ThemeToggle />
              {user && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/settings')}
                    className="rounded-full hidden md:flex hover:bg-muted"
                  >
                    <Settings className="h-5 w-5" />
                  </Button>
                  
                  <Sheet>
                    <SheetTrigger asChild>
                      <IconWithTooltip
                        icon={
                          <Button variant="ghost" size="icon" className="md:hidden">
                            <Menu className="h-5 w-5" />
                          </Button>
                        }
                        tooltip="Menu"
                        isClickable
                      />
                    </SheetTrigger>
                    <SheetPortal>
                      <SheetOverlay className="fixed inset-0 z-[200]" />
                      <SheetContent side="right" className="fixed z-[201] w-[300px] sm:w-[400px] p-6">
                        <SheetHeader>
                          <SheetTitle>
                            <Typography variant="h3">Menu</Typography>
                          </SheetTitle>
                        </SheetHeader>
                        <div className="flex flex-col gap-6 mt-8">
                          <nav className="flex flex-col gap-4">
                            <NavLinks />
                          </nav>
                          <div className="flex flex-col gap-4">
                            <Button
                              variant="ghost"
                              size="default"
                              onClick={() => navigate('/settings')}
                              className="w-full justify-start"
                            >
                              <Settings className="h-5 w-5 mr-2" />
                              <Typography variant="small">Settings</Typography>
                            </Button>
                            <Button
                              variant="ghost"
                              size="default"
                              onClick={handleSignOut}
                              className="w-full justify-start text-destructive hover:text-destructive"
                            >
                              <LogOut className="h-5 w-5 mr-2" />
                              <Typography variant="small">Sign out</Typography>
                            </Button>
                          </div>
                        </div>
                        <SheetClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                          <X className="h-4 w-4" />
                          <span className="sr-only">Close</span>
                        </SheetClose>
                      </SheetContent>
                    </SheetPortal>
                  </Sheet>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
});

Header.displayName = 'Header';

export { Header };